import { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bug,
  HelpCircle,
  Lightbulb,
  Heart,
  MessageSquare,
  Inbox,
  CheckCircle2,
  Clock,
  Trash2,
  ExternalLink,
  X as XIcon,
  RefreshCcw,
  User as UserIcon,
} from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const TYPE_META = {
  bug: { label: "Bug", Icon: Bug, tint: "clay" },
  confusing: { label: "Confusing", Icon: HelpCircle, tint: "gold" },
  suggestion: { label: "Suggestion", Icon: Lightbulb, tint: "sage" },
  praise: { label: "Praise", Icon: Heart, tint: "forest" },
  other: { label: "Other", Icon: MessageSquare, tint: "moss" },
};

const STATUS_META = {
  new: { label: "New", tint: "clay" },
  in_review: { label: "In review", tint: "gold" },
  planned: { label: "Planned", tint: "sage" },
  resolved: { label: "Resolved", tint: "forest" },
  wont_fix: { label: "Won't fix", tint: "moss" },
};

const STATUS_OPTIONS = Object.keys(STATUS_META);
const TYPE_OPTIONS = Object.keys(TYPE_META);

function tintClass(tint) {
  // Phase 42 — final contrast bump for the small admin status/type chips.
  // The prior 0.28 background opacity still read as "washed" on real
  // screens (especially the sage/pale-green chip). Lifted to 0.42 with a
  // stronger border, and every tint now uses a saturated dark text token
  // in light mode / cream in dark. StatCards on the summary row share
  // this class so they inherit the same readable presence.
  switch (tint) {
    case "clay":
      return "bg-[hsl(var(--sb-clay)/0.42)] text-[hsl(var(--sb-clay-2))] border-[hsl(var(--sb-clay)/0.7)] dark:text-[hsl(40_45%_92%)]";
    case "gold":
      return "bg-[hsl(var(--sb-gold)/0.42)] text-[hsl(var(--sb-gold-2))] border-[hsl(var(--sb-gold)/0.7)] dark:text-[hsl(40_45%_92%)]";
    case "sage":
      return "bg-[hsl(var(--sb-sage)/0.42)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-sage)/0.7)] dark:text-[hsl(40_45%_92%)]";
    case "forest":
      return "bg-[hsl(var(--sb-forest)/0.32)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest)/0.65)] dark:text-[hsl(40_45%_92%)]";
    default:
      return "bg-muted text-foreground border-border";
  }
}

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const isAdmin = (user?.roles || []).includes("admin");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      const [listRes, statsRes] = await Promise.all([
        api.get("/feedback", { params }),
        api.get("/feedback/stats"),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin, load]);

  if (user === undefined) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const selected = items.find((f) => f.id === selectedId) || null;

  const patchStatus = async (id, status, admin_note) => {
    try {
      const { data } = await api.patch(`/feedback/${id}`, { status, admin_note });
      setItems((prev) => prev.map((f) => (f.id === id ? data : f)));
      toast.success(`Marked as ${STATUS_META[status].label}`);
      // Reload stats
      const { data: s } = await api.get("/feedback/stats");
      setStats(s);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback permanently? This can't be undone.")) return;
    try {
      await api.delete(`/feedback/${id}`);
      setItems((prev) => prev.filter((f) => f.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Feedback deleted");
      const { data: s } = await api.get("/feedback/stats");
      setStats(s);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <SiteShell>
      <section className="container-page py-10" data-testid="admin-feedback-root">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-4xl tracking-tight">Beta Feedback</h1>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Every submission from the floating &quot;Report Feedback&quot; button. Filter, triage, and mark as resolved as you prepare for launch.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-feedback-refresh"
            className="btn-ghost inline-flex items-center gap-2"
          >
            <RefreshCcw size={16} /> Refresh
          </button>
        </div>

        {/* Stat cards */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-feedback-stats">
            <StatCard label="Total" value={stats.total} Icon={Inbox} tint="forest" />
            <StatCard label="New" value={stats.by_status?.new || 0} Icon={Clock} tint="clay" testid="stat-status-new" />
            <StatCard label="In review" value={stats.by_status?.in_review || 0} Icon={RefreshCcw} tint="gold" />
            <StatCard label="Planned" value={stats.by_status?.planned || 0} Icon={Lightbulb} tint="sage" />
            <StatCard label="Resolved" value={stats.by_status?.resolved || 0} Icon={CheckCircle2} tint="forest" />
          </div>
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <select
            data-testid="admin-feedback-filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="sb-input w-auto"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
          <select
            data-testid="admin-feedback-filter-type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="sb-input w-auto"
          >
            <option value="">All types</option>
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{TYPE_META[t].label}</option>
            ))}
          </select>
          {(filterStatus || filterType) && (
            <button
              type="button"
              onClick={() => { setFilterStatus(""); setFilterType(""); }}
              className="text-sm text-muted-foreground hover:underline"
              data-testid="admin-feedback-clear-filters"
            >
              Clear filters
            </button>
          )}
          <div className="ml-auto text-sm text-muted-foreground">
            {loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {/* List */}
        <div className="mt-6">
          {loading ? (
            <div className="text-center py-16 text-muted-foreground">Loading feedback…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed rounded-2xl" data-testid="admin-feedback-empty">
              <Inbox size={40} className="mx-auto text-muted-foreground" />
              <p className="mt-3 font-medium">No feedback yet</p>
              <p className="text-sm text-muted-foreground">Submissions from the floating button will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((f) => (
                <FeedbackRow
                  key={f.id}
                  feedback={f}
                  onSelect={() => setSelectedId(f.id)}
                  onStatus={patchStatus}
                  onDelete={remove}
                />
              ))}
            </div>
          )}
        </div>

        {selected && (
          <FeedbackDetail
            feedback={selected}
            onClose={() => setSelectedId(null)}
            onStatus={patchStatus}
            onDelete={remove}
            onReplied={(updated) => setItems((prev) => prev.map((f) => (f.id === updated.id ? updated : f)))}
          />
        )}
      </section>
    </SiteShell>
  );
}

function StatCard({ label, value, Icon, tint, testid }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${tintClass(tint)}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
        <Icon size={16} />
      </div>
      <div className="mt-1 font-serif text-3xl leading-none">{value}</div>
    </div>
  );
}

function FeedbackRow({ feedback, onSelect, onStatus, onDelete }) {
  const typeMeta = TYPE_META[feedback.type] || TYPE_META.other;
  const statusMeta = STATUS_META[feedback.status] || STATUS_META.new;
  const TypeIcon = typeMeta.Icon;

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 flex items-start gap-4 hover:shadow-md transition-shadow"
      data-testid="admin-feedback-item"
    >
      <div className={`shrink-0 w-10 h-10 rounded-xl border grid place-items-center ${tintClass(typeMeta.tint)}`}>
        <TypeIcon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-2">
          <span className={`chip border text-[11px] ${tintClass(typeMeta.tint)}`}>
            {typeMeta.label}
          </span>
          <span className={`chip border text-[11px] ${tintClass(statusMeta.tint)}`} data-testid={`admin-feedback-status-${feedback.id}`}>
            {statusMeta.label}
          </span>
          {feedback.user_email ? (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <UserIcon size={12} /> {feedback.user_display_name || feedback.user_email}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground italic">Anonymous</span>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {new Date(feedback.created_at).toLocaleString()}
          </span>
        </div>
        <button
          type="button"
          onClick={onSelect}
          data-testid={`admin-feedback-open-${feedback.id}`}
          className="mt-2 text-left w-full"
        >
          <p className="text-sm line-clamp-2">{feedback.description}</p>
          <div className="mt-1 text-xs text-muted-foreground truncate">
            <ExternalLink size={11} className="inline mr-1" />
            <code className="font-mono">{feedback.page_url}</code>
          </div>
        </button>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={feedback.status}
            onChange={(e) => onStatus(feedback.id, e.target.value, feedback.admin_note || undefined)}
            data-testid={`admin-feedback-status-select-${feedback.id}`}
            className="sb-input py-1 text-xs w-auto"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onSelect}
            className="text-xs font-medium text-[hsl(var(--sb-forest-2))] underline underline-offset-2 hover:no-underline"
          >
            View details
          </button>
          <button
            type="button"
            onClick={() => onDelete(feedback.id)}
            data-testid={`admin-feedback-delete-${feedback.id}`}
            className="ml-auto text-xs text-[hsl(var(--sb-clay-2))] inline-flex items-center gap-1 hover:underline"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedbackDetail({ feedback, onClose, onStatus, onDelete, onReplied }) {
  const [note, setNote] = useState(feedback.admin_note || "");
  const [status, setStatus] = useState(feedback.status);
  const [saving, setSaving] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const typeMeta = TYPE_META[feedback.type] || TYPE_META.other;

  const screenshotUrl = useMemo(() => {
    if (!feedback.screenshot_url) return null;
    return `${process.env.REACT_APP_BACKEND_URL}${feedback.screenshot_url}`;
  }, [feedback.screenshot_url]);

  const save = async () => {
    setSaving(true);
    try {
      await onStatus(feedback.id, status, note);
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    const body = replyBody.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/feedback/${feedback.id}/reply`, { body });
      setReplyBody("");
      toast.success("Reply sent — the user has been notified.");
      onReplied?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-feedback-detail"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full sm:max-w-2xl bg-card border border-border rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-border/60 bg-[hsl(var(--sb-forest)/0.03)]">
          <div className="min-w-0 flex items-center gap-3">
            <div className={`shrink-0 w-10 h-10 rounded-xl border grid place-items-center ${tintClass(typeMeta.tint)}`}>
              <typeMeta.Icon size={18} />
            </div>
            <div className="min-w-0">
              <h2 className="font-serif text-xl">{typeMeta.label} feedback</h2>
              <p className="text-xs text-muted-foreground">
                {new Date(feedback.created_at).toLocaleString()}
                {feedback.updated_at && feedback.updated_at !== feedback.created_at && (
                  <> · updated {new Date(feedback.updated_at).toLocaleString()}</>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-feedback-detail-close"
            aria-label="Close"
            className="rounded-full p-2 hover:bg-muted text-muted-foreground shrink-0"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">From</div>
            <div className="mt-1 text-sm">
              {feedback.user_email ? (
                <>
                  <span className="font-medium">{feedback.user_display_name || feedback.user_email}</span>
                  {feedback.user_display_name && (
                    <span className="text-muted-foreground"> · {feedback.user_email}</span>
                  )}
                </>
              ) : (
                <span className="italic text-muted-foreground">Anonymous visitor</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Description</div>
            <p className="mt-1 text-sm whitespace-pre-wrap" data-testid="admin-feedback-detail-description">{feedback.description}</p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Page</div>
            <a
              href={feedback.page_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 text-sm text-[hsl(var(--sb-forest-2))] hover:underline break-all inline-flex items-center gap-1"
            >
              {feedback.page_url} <ExternalLink size={12} />
            </a>
          </div>

          {feedback.user_agent && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Browser</div>
              <p className="mt-1 text-xs font-mono break-all text-muted-foreground">{feedback.user_agent}</p>
            </div>
          )}

          {screenshotUrl && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Screenshot</div>
              <a href={screenshotUrl} target="_blank" rel="noreferrer" className="mt-2 block">
                <img
                  src={screenshotUrl}
                  alt="Feedback screenshot"
                  className="rounded-xl border border-border max-h-96 object-contain w-full bg-muted"
                  data-testid="admin-feedback-screenshot"
                />
              </a>
            </div>
          )}

          <div className="border-t border-border/60 pt-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Reply thread</div>
            <div className="space-y-2" data-testid="admin-feedback-detail-replies">
              {(feedback.replies || []).length === 0 && (
                <div className="text-xs text-muted-foreground italic">No replies yet.</div>
              )}
              {(feedback.replies || []).map((r) => {
                const mine = r.author_role === "admin";
                return (
                  <div
                    key={r.id}
                    className={`rounded-lg border p-3 text-sm ${
                      mine
                        ? "bg-[hsl(var(--sb-forest))/0.06] border-[hsl(var(--sb-forest))]/25"
                        : "bg-muted/40 border-border"
                    }`}
                    data-testid={`admin-feedback-reply-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-[11px] font-semibold">
                        {mine ? "Admin" : "Submitter"} · {r.author_display_name || "—"}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap break-words">{r.body}</div>
                  </div>
                );
              })}
            </div>
            {feedback.user_id ? (
              <>
                <label className="text-sm font-medium mt-3 block" htmlFor="admin-reply">Reply to submitter</label>
                <textarea
                  id="admin-reply"
                  data-testid="admin-feedback-detail-reply-body"
                  rows={3}
                  maxLength={4000}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Write a reply — the user will be notified and can respond back."
                  className="sb-input mt-1 resize-none"
                />
                <div className="mt-2 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={sending || !replyBody.trim()}
                    data-testid="admin-feedback-detail-reply-send"
                    className="btn-primary"
                  >
                    {sending ? "Sending…" : "Send reply"}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-3 text-xs text-muted-foreground italic">
                Anonymous submission — replies cannot be delivered.
              </div>
            )}
          </div>

          <div className="border-t border-border/60 pt-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Triage</div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="admin-feedback-detail-status"
                className="sb-input w-auto"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            </div>
            <label className="text-sm font-medium mt-3 block" htmlFor="admin-note">Internal note</label>
            <textarea
              id="admin-note"
              data-testid="admin-feedback-detail-note"
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Only visible to admins — link to a ticket, jot a decision, etc."
              className="sb-input mt-1 resize-none"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 p-4 bg-[hsl(var(--sb-forest)/0.02)]">
          <button
            type="button"
            onClick={() => onDelete(feedback.id)}
            data-testid="admin-feedback-detail-delete"
            className="btn-ghost text-[hsl(var(--sb-clay-2))] inline-flex items-center gap-1"
          >
            <Trash2 size={14} /> Delete
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <button type="button" onClick={onClose} className="btn-ghost">Close</button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="admin-feedback-detail-save"
              className="btn-primary"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
